import { useEffect, useRef } from 'react';

import useInternalMixpanel from '@/hooks/useTracking/useInternalMixpanel';
import { ToastMessage } from '@/types/appMessage';
import { StaffUserSession } from '@/types/staffSession';
import { UserPermissions } from '@/types/user';
import chromeExtensionVersion from '@/utils/chromeExtensionVersion';
import isInChromeExtension from '@/utils/isInChromeExtension';

interface Props {
  currentUser: StaffUserSession | null;
  permissions: UserPermissions | undefined;
  messages: ToastMessage[];
}

/**
 * Side-effect-only bridge that fires Dugout's internal Mixpanel events (toasts, the chrome
 * sidebar boot signal, and the current user's role as a super property). It has to be its own
 * component because it must render *under* InternalMixpanelProvider, while DugoutApp - which owns
 * currentUser/permissions/messages - renders that provider as one of its own descendants.
 */
const DugoutTelemetry = ({ currentUser, permissions, messages }: Props) => {
  const { registerSuperProperties, identify, reset, trackToast, trackChromeSidebarOpened } =
    useInternalMixpanel();
  const seenToastIds = useRef(new Set<string>());
  const sidebarOpenTracked = useRef(false);
  const identifiedUserId = useRef<string | null>(null);

  useEffect(() => {
    if (sidebarOpenTracked.current) {
      return;
    }
    sidebarOpenTracked.current = true;

    if (isInChromeExtension()) {
      trackChromeSidebarOpened(chromeExtensionVersion());
    }
  }, [trackChromeSidebarOpened]);

  useEffect(() => {
    if (currentUser && permissions) {
      identifiedUserId.current = currentUser.id;
      registerSuperProperties({
        role: currentUser.primary_role?.slug ?? null,
        isSuperUser: permissions.is_super_user
      });
      identify(currentUser.id);
    } else if (!currentUser && identifiedUserId.current) {
      identifiedUserId.current = null;
      reset();
    }
  }, [currentUser, permissions, identify, registerSuperProperties, reset]);

  useEffect(() => {
    messages.forEach((message) => {
      if (!seenToastIds.current.has(message.id)) {
        seenToastIds.current.add(message.id);
        trackToast(message);
      }
    });
  }, [messages, trackToast]);

  return null;
};

export default DugoutTelemetry;
